import { useRef, useState, type ReactNode } from "react";
import { Loader2, ArrowLeft } from "lucide-react";
import { useScrollIntoViewOn, useKeyboardAwareForm } from "@/hooks/use-form-viewport";

export interface WizardStep {
  /** Short label shown in the progress row. */
  title: string;
  /** The fields for this step. Keep it short enough to fit one screen. */
  content: ReactNode;
  /**
   * Return true to advance. Return false (after surfacing the error) to stay put;
   * the wizard will scroll the step back to the top so the message is visible.
   * For react-hook-form: `() => form.trigger(["email", "phone"])`.
   */
  validate?: () => boolean | Promise<boolean>;
}

interface FormWizardProps {
  steps: WizardStep[];
  /** Called after the final step validates. Wire to your submit handler. */
  onComplete: () => void | Promise<void>;
  submitLabel?: string;
  submittingLabel?: string;
  isSubmitting?: boolean;
  className?: string;
}

/**
 * Guided, app-like multi-step form. Each step shows only what fits one screen;
 * advancing, going back, or hitting a validation error re-anchors the step to the
 * top of the visible area (below the sticky header). Entered values are owned by
 * the caller's form state, so they persist across steps automatically.
 */
export function FormWizard({
  steps,
  onComplete,
  submitLabel = "Submit",
  submittingLabel = "Submitting...",
  isSubmitting = false,
  className,
}: FormWizardProps) {
  const [step, setStep] = useState(0);
  const [errorTick, setErrorTick] = useState(0);
  const shellRef = useRef<HTMLDivElement>(null);

  useScrollIntoViewOn(shellRef, `${step}:${errorTick}`);
  useKeyboardAwareForm(shellRef);

  const isLast = step === steps.length - 1;
  const current = steps[step];

  const handleNext = async () => {
    const ok = current.validate ? await current.validate() : true;
    if (!ok) {
      setErrorTick((n) => n + 1); // re-anchor so the error shows
      return;
    }
    if (isLast) {
      await onComplete();
    } else {
      setStep((s) => Math.min(steps.length - 1, s + 1));
    }
  };

  const handleBack = () => setStep((s) => Math.max(0, s - 1));

  return (
    <div
      ref={shellRef}
      className={className}
      style={{ scrollMarginTop: "calc(var(--app-header-h, 72px) + 12px)" }}
    >
      {/* Progress */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <p className="text-luxury-xs font-sans uppercase tracking-[0.2em] text-driftwood">
            Step {step + 1} of {steps.length}
          </p>
          <p className="text-luxury-xs font-sans text-ink-soft">{current.title}</p>
        </div>
        <div className="flex gap-1.5" role="progressbar" aria-label={`Step ${step + 1} of ${steps.length}`} aria-valuenow={step + 1} aria-valuemin={1} aria-valuemax={steps.length}>
          {steps.map((s, i) => (
            <span
              key={s.title + i}
              className={`h-1 flex-1 transition-colors duration-300 ${i <= step ? "bg-ink" : "bg-sand-deep"}`}
            />
          ))}
        </div>
      </div>

      {/* Active step */}
      <div>{current.content}</div>

      {/* Navigation — always directly under the fields, never off-screen */}
      <div className="mt-6 flex items-center gap-3">
        {step > 0 && (
          <button
            type="button"
            onClick={handleBack}
            disabled={isSubmitting}
            className="inline-flex items-center justify-center gap-1.5 border border-ink px-5 h-12 font-sans font-normal text-[13px] tracking-[0.08em] uppercase text-ink hover:bg-ink hover:text-shell transition-colors disabled:opacity-50"
            data-testid="wizard-back"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Back
          </button>
        )}
        <button
          type="button"
          onClick={handleNext}
          disabled={isSubmitting}
          className="flex-1 inline-flex items-center justify-center gap-2 bg-sun text-shell hover:bg-sun-hover px-6 h-12 font-sans font-normal text-[13px] tracking-[0.1em] uppercase transition-colors disabled:opacity-60"
          data-testid={isLast ? "wizard-submit" : "wizard-next"}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              {submittingLabel}
            </>
          ) : isLast ? (
            submitLabel
          ) : (
            "Continue"
          )}
        </button>
      </div>
    </div>
  );
}
